import type { LucideIcon } from 'lucide-react'
import {
  Calculator,
  FileJson2,
  FileStack,
  Image,
  Images,
  Landmark,
  Maximize2,
  Minimize2,
  QrCode,
  ScanLine,
  Scissors,
  WandSparkles,
} from 'lucide-react'

export type ToolCategory = 'Calculate' | 'Image' | 'PDF' | 'Text'

export type ToolDefinition = {
  id: string
  name: string
  description: string
  category: ToolCategory
  keywords: readonly string[]
  icon: LucideIcon
}

export const tools: readonly ToolDefinition[] = [
  { id: 'sip-calculator', name: 'SIP Calculator', description: 'Project a monthly investment.', category: 'Calculate', keywords: ['investment', 'mutual fund', 'monthly', 'returns', 'goal', 'step up', 'compare'], icon: Landmark },
  { id: 'cagr-calculator', name: 'Lumpsum & CAGR', description: 'Grow a lump sum or find CAGR.', category: 'Calculate', keywords: ['return', 'growth', 'investment', 'lumpsum', 'target', 'inflation'], icon: Calculator },
  { id: 'emi-calculator', name: 'EMI Calculator', description: 'Estimate a loan payment.', category: 'Calculate', keywords: ['loan', 'monthly payment', 'interest', 'mortgage', 'prepayment', 'tenure', 'compare'], icon: Calculator },
  { id: 'image-compress', name: 'Compress Image', description: 'Reduce image file size.', category: 'Image', keywords: ['photo size', 'shrink', 'optimize', 'jpeg', '200kb'], icon: Minimize2 },
  { id: 'image-resize', name: 'Resize Image', description: 'Change photo dimensions.', category: 'Image', keywords: ['photo dimensions', 'pixels', 'scale', 'instagram', 'whatsapp'], icon: Maximize2 },
  { id: 'image-convert', name: 'Convert Image', description: 'Switch image formats.', category: 'Image', keywords: ['png', 'jpeg', 'webp', 'format'], icon: Image },
  { id: 'image-remove-metadata', name: 'Remove Metadata', description: 'Strip location and camera data.', category: 'Image', keywords: ['exif', 'strip', 'privacy', 'location'], icon: ScanLine },
  { id: 'pdf-merge', name: 'Merge PDF', description: 'Combine PDFs into one file.', category: 'PDF', keywords: ['combine pdf', 'join', 'documents'], icon: FileStack },
  { id: 'pdf-split', name: 'Split PDF', description: 'Extract selected pages.', category: 'PDF', keywords: ['extract pages', 'separate pdf', 'range'], icon: Scissors },
  { id: 'images-to-pdf', name: 'Images to PDF', description: 'Turn photos into a PDF.', category: 'PDF', keywords: ['photo pdf', 'convert images', 'document', 'a4', 'letter'], icon: Images },
  { id: 'json-format', name: 'Format JSON', description: 'Validate, format, or minify.', category: 'Text', keywords: ['pretty json', 'beautify', 'validate json', 'minify'], icon: FileJson2 },
  { id: 'qr-generate', name: 'QR Generator', description: 'Make a code for a link or Wi-Fi.', category: 'Text', keywords: ['qr code', 'url', 'scan', 'wifi', 'vcard', 'calendar'], icon: QrCode },
]

export const categories = ['All', 'Calculate', 'Image', 'PDF', 'Text'] as const
export type CategoryFilter = (typeof categories)[number]

export function findTool(id: string): ToolDefinition | undefined {
  return tools.find((tool) => tool.id === id)
}

function scoreTool(tool: ToolDefinition, rawQuery: string): number {
  const query = rawQuery.trim().toLowerCase()
  if (!query) return 1

  const name = tool.name.toLowerCase()
  const haystack = [tool.description, tool.category, ...tool.keywords].join(' ').toLowerCase()
  if (name === query) return 100
  if (name.startsWith(query)) return 80
  if (name.includes(query)) return 60
  if (haystack.includes(query)) return 40

  const tokens = query.split(/\s+/).filter(Boolean)
  return tokens.every((token) => `${name} ${haystack}`.includes(token)) ? 20 : 0
}

export function searchTools(query: string, category: CategoryFilter = 'All'): ToolDefinition[] {
  return tools
    .filter((tool) => category === 'All' || tool.category === category)
    .map((tool) => ({ tool, score: scoreTool(tool, query) }))
    .filter(({ score }) => score > 0)
    .sort((left, right) => right.score - left.score || left.tool.name.localeCompare(right.tool.name))
    .map(({ tool }) => tool)
}

export const featuredToolIds = ['image-compress', 'sip-calculator', 'pdf-merge', 'json-format'] as const
export const recentToolIds = ['image-resize', 'qr-generate', 'emi-calculator'] as const
export const placeholderIcon = WandSparkles
